import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Header, Footer, Seo } from "../../../components";
import { PaginationNav } from "../../../components/PaginationNav";
import { column } from "../../../store";
import { OG_IMAGE, canonicalUrl } from "../../../utils/seo";
import searchIcon from "../../../icons/search.svg";
import { FaqHero } from "../FaqHero";
import { FaqListItem } from "../FaqListItem";
import { mapColumnToFaqItem } from "../faqPageUtils";
import {
  FAQ_CATEGORY_BY_SLUG,
  FAQ_CATEGORIES,
  classifyColumnCategory,
} from "../faqCategories";
import { FAQ_POSTS_PER_PAGE, FAQ_SEARCH_DEBOUNCE_MS } from "../faqPageConfig";
import "../style.css";
import "./FaqCategory.css";

export const FaqCategory = () => {
  const { categorySlug } = useParams();
  const [currentPage, setCurrentPage] = useState(1);
  // searchInput: 입력창 즉시 값 / searchKeyword: 디바운스 후 실제 필터에 쓰는 값
  const [searchInput, setSearchInput] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");

  const category = FAQ_CATEGORY_BY_SLUG[categorySlug];

  const { allColumns, isLoading, getAllColumns } = column((state) => ({
    allColumns: state.allColumns,
    isLoading: state.isLoading,
    getAllColumns: state.getAllColumns,
  }));

  useEffect(() => {
    getAllColumns();
  }, [getAllColumns]);

  // 카테고리가 바뀌면 검색·페이지 초기화
  useEffect(() => {
    setCurrentPage(1);
    setSearchInput("");
    setSearchKeyword("");
  }, [categorySlug]);

  // 검색어 디바운스(FAQ 목록과 동일 동작)
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchKeyword(searchInput);
      setCurrentPage(1);
    }, FAQ_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // 카테고리에 속하는 글만 필터(제목/해시태그 키워드 매칭) + 최신순
  const categoryItems = useMemo(() => {
    if (!category || !Array.isArray(allColumns)) return [];
    return allColumns
      .filter(
        (c) => classifyColumnCategory(c.title, c.hashtags) === category.slug
      )
      .map(mapColumnToFaqItem);
  }, [allColumns, category]);

  // 카테고리 내 검색(제목·해시태그 부분일치)
  const filteredItems = useMemo(() => {
    const kw = searchKeyword.trim().toLowerCase();
    if (!kw) return categoryItems;
    return categoryItems.filter((item) => {
      const haystack = `${item.title} ${(item.hashtags ?? []).join(" ")}`.toLowerCase();
      return haystack.includes(kw);
    });
  }, [categoryItems, searchKeyword]);

  const totalPages =
    Math.ceil(filteredItems.length / FAQ_POSTS_PER_PAGE) || 1;

  const pageItems = useMemo(() => {
    const start = (currentPage - 1) * FAQ_POSTS_PER_PAGE;
    return filteredItems.slice(start, start + FAQ_POSTS_PER_PAGE);
  }, [filteredItems, currentPage]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    document
      .querySelector(".faq-board-page__content")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // 잘못된 카테고리 slug 는 FAQ 목록으로 보낸다.
  if (!category) {
    return <Navigate to="/faq" replace />;
  }

  const canonical = canonicalUrl(`/faq/category/${category.slug}`);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        "@id": `${canonical}#collection`,
        url: canonical,
        name: category.h1,
        description: category.metaDescription,
        inLanguage: "ko-KR",
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${canonical}#breadcrumb`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "홈", item: canonicalUrl("/") },
          {
            "@type": "ListItem",
            position: 2,
            name: "자주 묻는 질문",
            item: canonicalUrl("/faq"),
          },
          { "@type": "ListItem", position: 3, name: category.h1, item: canonical },
        ],
      },
    ],
  };

  return (
    <>
      <Seo
        title={category.title}
        description={category.metaDescription}
        ogTitle={`${category.h1}｜희망과학`}
        ogDescription={category.metaDescription}
        image={OG_IMAGE}
        canonical={canonical}
        jsonLd={jsonLd}
      />
      <div className="faq-board-page-shell">
        <Header variant="dark" />

        <main className="faq-board-page">
          {/* FAQ 목록과 동일한 히어로 — 문구만 카테고리별(H1·소개문)로 교체 */}
          <FaqHero
            label="법률정보"
            title={category.h1}
            description={category.intro}
          />

          <section
            className="faq-board-page__content"
            aria-label={`${category.h1} 목록`}
          >
            <div className="faq-board-page__inner">
              <div className="faq-board-toolbar">
                <form
                  className="faq-board-toolbar__search"
                  role="search"
                  onSubmit={(e) => e.preventDefault()}
                >
                  <img
                    src={searchIcon}
                    alt=""
                    className="faq-board-toolbar__search-icon"
                  />
                  <input
                    type="search"
                    className="faq-board-toolbar__search-input"
                    placeholder="질문·내용 검색"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    aria-label={`${category.h1} 검색`}
                  />
                </form>

                {/* 분야별(카테고리) 바로가기 — FAQ 목록과 동일 위치·스타일 */}
                <nav
                  className="faq-board-toolbar__categories"
                  aria-label="법률정보 카테고리"
                >
                  <Link to="/faq" className="faq-category-nav__chip">
                    전체
                  </Link>
                  {FAQ_CATEGORIES.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/faq/category/${c.slug}`}
                      className={`faq-category-nav__chip${
                        c.slug === category.slug
                          ? " faq-category-nav__chip--active"
                          : ""
                      }`}
                      aria-current={c.slug === category.slug ? "page" : undefined}
                    >
                      {c.navLabel}
                    </Link>
                  ))}
                </nav>
              </div>

              <p className="faq-board-page__count">
                총 {filteredItems.length.toLocaleString()}건
              </p>

              <div className="faq-board-list">
                {isLoading ? (
                  // 로딩 중에도 목록과 동일한 높이의 스켈레톤을 렌더해
                  // 데이터 도착 시 레이아웃이 튀지 않게 한다.
                  <div
                    className="faq-board-list__skeleton"
                    role="status"
                    aria-label="불러오는 중"
                  >
                    {Array.from({ length: FAQ_POSTS_PER_PAGE }).map((_, i) => (
                      <div className="faq-board-skeleton__row" key={i}>
                        <span className="faq-board-skeleton__bar faq-board-skeleton__bar--title" />
                        <span className="faq-board-skeleton__bar faq-board-skeleton__bar--tag" />
                        <span className="faq-board-skeleton__bar faq-board-skeleton__bar--date" />
                      </div>
                    ))}
                  </div>
                ) : pageItems.length > 0 ? (
                  pageItems.map((item) => (
                    <FaqListItem key={item.id} item={item} />
                  ))
                ) : (
                  <p className="faq-board-page__empty" role="status">
                    {searchKeyword.trim()
                      ? "조건에 해당하는 질문이 없습니다."
                      : "해당 분야의 게시글이 아직 없습니다."}
                  </p>
                )}
              </div>

              {!isLoading && filteredItems.length > 0 && (
                <PaginationNav
                  className="faq-board-page__pagination"
                  ariaLabel={`${category.h1} 페이지`}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              )}
            </div>
          </section>
        </main>
      </div>

      <Footer />
    </>
  );
};
